import { useSelector } from 'react-redux';
import { formatSlotToISO, formatDateToISO } from '../utils/helpers';
import { RootState } from '../redux/store';

interface TimeSlotSelectionProps {
  selectedSlot: string | null;
  onSlotSelect: (slotId: string) => void;
  slotsByDate?: Record<string, Record<string, string[]>>;
  onPrevWeek?: () => void;
  onNextWeek?: () => void;
  currentStart: Date;
  onOperatoriesSelect?: (operatories: string[]) => void;
  daysToShow?: number;
}

const TimeSlotSelection = ({
  selectedSlot,
  onSlotSelect,
  slotsByDate,
  onPrevWeek,
  onNextWeek,
  currentStart,
  onOperatoriesSelect,
  daysToShow = 7,
}: TimeSlotSelectionProps) => {
  const { treatment } = useSelector((s: RootState) => s.appointment);
  const getVisibleDates = () => {
    const dates: Date[] = [];
    for (let i = 0; i < daysToShow; i++) {
      const d = new Date(currentStart);
      d.setDate(currentStart.getDate() + i);
      dates.push(d);
    }
    return dates;
  };
  const visibleDates = getVisibleDates();

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSlotHHMM = (slot: string) => (slot || '').split(' ')[0] || '';

  //TO DO: handle it on backend side
  const isSlotInFuture = (dateStr: string, slot: string): boolean => {
    const now = new Date();
    // Add buffer
    now.setMinutes(now.getMinutes() + (treatment?.bufferMinutes || 0));
    const slotTime = getSlotHHMM(slot);
    const [hourStr, minuteStr] = slotTime.split(':');
    const hour = parseInt(hourStr || '0', 10);
    const minute = parseInt(minuteStr || '0', 10);

    // Parse the date string (format: YYYY-MM-DD)
    const [year, month, day] = dateStr.split('-').map(n => parseInt(n, 10));
    const slotDate = new Date(year, month - 1, day, hour, minute);

    return slotDate > now;
  };

  const formatTimeSlot = (slot: string) => {
    const hhmm = getSlotHHMM(slot);
    const [hStr, mStr] = hhmm.split(':');
    const hour24 = parseInt(hStr || '0', 10);
    const minutes = parseInt(mStr || '0', 10);
    const isPM = hour24 >= 12;
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const minutesPadded = String(minutes).padStart(2, '0');
    return `${hour12}:${minutesPadded} ${isPM ? 'pm' : 'am'}`;
  };

  const getDayName = (date: Date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const getDayNumber = (date: Date) => {
    return date.getDate();
  };


  return (
    <div className="space-y-4">
      <div className="">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={onPrevWeek}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-teamcare-yellow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Previous
          </button>

          <h3 className="text-sm font-semibold text-gray-900">
            {daysToShow < 7
              ? visibleDates[0]?.getFullYear()
              : `${formatDate(visibleDates[0])} - ${formatDate(visibleDates[visibleDates.length - 1])}`
            }
          </h3>

          <button
            onClick={onNextWeek}
            className="flex items-center px-3 py-1 text-sm text-gray-600 hover:text-teamcare-yellow disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Next
          </button>
        </div>

        {/* Sticky Date Headers */}
        <div className={`grid gap-2 sticky top-0 bg-white z-10 pb-2 ${daysToShow === 3 ? 'grid-cols-3' : 'grid-cols-7'}`}>
          {visibleDates.map((date, index) => {
            return (
              <div key={index} className="text-center p-2 bg-black rounded-lg">
                <div className="text-xs font-medium text-white">
                  {getDayName(date)}
                </div>
                <div className="text-sm font-bold text-white">
                  {getDayNumber(date)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Time Slots Grid with Single Scroll */}
        <div className="max-h-72 overflow-y-auto">
          <div className={`grid gap-2 ${daysToShow === 3 ? 'grid-cols-3' : 'grid-cols-7'}`}>
            {visibleDates.map((date, index) => {
              const dateStr = formatDateToISO(date);
              const timeSlotsObj = slotsByDate?.[dateStr] || {};
              const timeSlots = Object.keys(timeSlotsObj)
                .filter((slot) => isSlotInFuture(dateStr, slot)) // Filter out historical slots
                .sort((a, b) => {
                  const [ah, am] = getSlotHHMM(a).split(':').map((n) => parseInt(n || '0', 10));
                  const [bh, bm] = getSlotHHMM(b).split(':').map((n) => parseInt(n || '0', 10));
                  return ah * 60 + am - (bh * 60 + bm);
                });

              return (
                <div key={index} className="space-y-1">
                  {/* Time Slots */}
                  {timeSlots.length === 0 ? (
                    <div className="text-center py-2 text-gray-400 text-xs">
                      Closed / No Slots Available
                    </div>
                  ) : (
                    timeSlots.map((slot: string) => {
                      const slotId = formatSlotToISO(dateStr, slot);
                      const isSelected = selectedSlot === slotId;
                      const operatories_available = timeSlotsObj[slot];

                      return (
                        <button
                          key={slot}
                          onClick={() => {
                            onSlotSelect(slotId);
                            if (onOperatoriesSelect) onOperatoriesSelect(operatories_available || []);
                          }}
                          className={`w-full px-2 py-3 text-xs rounded border transition-all duration-200 ${isSelected
                            ? 'bg-teamcare-yellow border-teamcare-yellow text-black font-medium shadow-md'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-teamcare-yellow hover:bg-teamcare-yellow/10'
                            }`}
                        >
                          {formatTimeSlot(slot)}
                        </button>
                      );
                    })
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSlotSelection;